"use client";

import { useTranslation } from "react-i18next";
import { FC, useCallback, useContext } from "react";
import {
    CasinoScopesMap,
    PrematchFeedContext,
    UseCasinoGamesHookOptions,
    useRuntimeConfig,
    useSiteConfig,
} from "@finbackoffice/site-core";
import {
    CasinoSectionsStyleEnum,
    HomePageSectionsEnum,
    IBannersWidgetProps,
    ILayoutsHomePageSection,
    ISiteConfigLayouts,
} from "@finbackoffice/fe-core";
import dynamic from "next/dynamic";
import { TranslationScopes, AssetsDirectory } from "@finbackoffice/enums";
import { HoursRange } from "@finbackoffice/websocket-client";
import { IBannerResponse } from "@finbackoffice/clientbff-client";
import { IReelsDataState } from "types/reels.data";
import PreloadComponent from "components/base/preload-component/PreloadComponent";
import { Svg } from "components/base/svg/Svg";
import { RouteNames } from "utils/constants";
import { getBannerSize } from "utils/helpers";
import { PrematchContext } from "contexts";
import { BannerSliderSliderSettings } from "components/base/banners-slider/BannersSlider";
import TopWinners from "components/casino/widgets/top-winners/TopWinners";
import ErrorBoundary from "components/base/error-boundary/ErrorBoundary";
import CasinoGames from "../casino/widgets/games/CasinoGames";
import HomeFAQ from "./faq/HomeFAQ";
import SportGames from "./sport-games/SportGames";
import styles from "./home.module.sass";

const HomeTournament = dynamic(() => import("./tournament/HomeTournament"), {
    loading: () => <PreloadComponent style={{ height: 190, marginBottom: 8 }} />,
    ssr: false,
});

const Reels = dynamic(() => import("components/casino/widgets/reels/Reels"), {
    loading: () => <PreloadComponent style={{ height: 240 }} />,
    ssr: false,
});

const BannersSlider = dynamic(() => import("components/base/banners-slider/BannersSlider"), {
    loading: () => <PreloadComponent style={{ height: 248, marginBottom: 8 }} />,
    ssr: false,
});

type IProps = {
    reels: IReelsDataState | null;
    banners: IBannerResponse[];
};

const Home: FC<IProps> = ({ reels, banners }) => {
    const COMMON_SITE_CONFIGS = useRuntimeConfig("COMMON_SITE_CONFIGS");
    const { t } = useTranslation(TranslationScopes.PreMatch);
    const homePageConfig = useSiteConfig<ISiteConfigLayouts>("layouts").homePage;
    const { prematchTopLeagues } = useContext(PrematchFeedContext);
    const { setHoursRange } = useContext(PrematchContext);
    const hasTopLeagues = prematchTopLeagues.length > 0;

    const getBannerSettings = useCallback(
        (
            sectionConfig: IBannersWidgetProps<HomePageSectionsEnum.Banners>,
        ): BannerSliderSliderSettings => {
            const result: BannerSliderSliderSettings = { options: {}, autoplay: {} };

            if (sectionConfig.sliderSpeed) {
                result.autoplay.delay = sectionConfig.sliderSpeed;
            }

            if (sectionConfig.transitionSpeed) {
                result.options.duration = sectionConfig.transitionSpeed;
            }
            return result;
        },
        [],
    );

    const renderSection = useCallback(
        (section: ILayoutsHomePageSection, index: number) => {
            if (section.enable) {
                switch (section.name) {
                    case HomePageSectionsEnum.Banners:
                        if (!banners.length) return null;
                        return (
                            <BannersSlider
                                key={index}
                                banners={banners}
                                sliderSettings={getBannerSettings(section)}
                                wrapperClassName={styles.homeBannerContainer}
                                imgProps={getBannerSize(section.size)}
                            />
                        );
                    case HomePageSectionsEnum.Slots:
                    case HomePageSectionsEnum.LiveCasino: {
                        const gameTypes = section.type || CasinoScopesMap[section.name];
                        const commonOptions: UseCasinoGamesHookOptions = {
                            megaways: section.prop === "megaways",
                            bonusbuy: section.prop === "bonusbuy",
                            has_jackpot: section.prop === "has_jackpot",
                        };

                        if (section.search) {
                            commonOptions.gameName = section.search;
                        }

                        return (
                            <CasinoGames
                                id={section.id}
                                key={index}
                                style={section.style}
                                tag={section.tag}
                                gameTypes={gameTypes}
                                provider={section.provider || "All"}
                                options={{
                                    ...commonOptions,
                                }}
                                href={section.href}
                                backImg={
                                    section.style === CasinoSectionsStyleEnum.RoundRow
                                        ? section.backImg
                                        : undefined
                                }
                                imgSrc={section.imgSrc}
                                imgFolder={COMMON_SITE_CONFIGS.casinoImg}
                            />
                        );
                    }
                    case HomePageSectionsEnum.Reels:
                        return (
                            reels !== null && (
                                <Reels key={index} reels={reels} wrapperClassName="homeWidget" />
                            )
                        );
                    case HomePageSectionsEnum.Sport:
                        if (section.top_leagues_index && hasTopLeagues) {
                            return section.top_leagues_index.map((leagueIndex) => {
                                const tournament = prematchTopLeagues[leagueIndex - 1];
                                return tournament ? (
                                    <HomeTournament key={tournament.id} tournament={tournament} />
                                ) : null;
                            });
                        }
                        break;
                    case HomePageSectionsEnum.PrematchTop:
                        return (
                            <SportGames
                                key={index}
                                icon={
                                    <Svg
                                        src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/top-leagues.svg`}
                                        className="svg-icon"
                                    />
                                }
                                title={t("prematch_top_matches")}
                                href={`/${RouteNames.SPORT}/${RouteNames.PREMATCH}/${RouteNames.TOP}`}
                                onSeeAllClick={() => setHoursRange(HoursRange.TWENTY_FOUR)}
                                type={section.name}
                                hoursRange={section.timeRange}
                                sliderSpeed={section.sliderSpeed}
                            />
                        );
                    case HomePageSectionsEnum.LiveUpcoming:
                        return (
                            <SportGames
                                key={index}
                                icon={
                                    <Svg
                                        src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/upcoming-matches.svg`}
                                        className="svg-icon"
                                    />
                                }
                                title={t("prematch_upcoming_live")}
                                href={`/${RouteNames.SPORT}/${RouteNames.UPCOMING}`}
                                type={section.name}
                                hoursRange={section.timeRange}
                                sliderSpeed={section.sliderSpeed}
                            />
                        );
                    case HomePageSectionsEnum.PrematchUpcoming:
                        return (
                            <SportGames
                                key={index}
                                icon={
                                    <Svg
                                        src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/upcoming-matches.svg`}
                                        className="svg-icon"
                                    />
                                }
                                title={t("prematch_upcoming_matches")}
                                href={`/${RouteNames.SPORT}/${RouteNames.PREMATCH}/${RouteNames.UPCOMING}`}
                                type={section.name}
                                hoursRange={section.timeRange}
                                sliderSpeed={section.sliderSpeed}
                            />
                        );
                    case HomePageSectionsEnum.Winners:
                        return (
                            <ErrorBoundary key={index} name={TopWinners.displayName}>
                                <TopWinners config={section.config} />
                            </ErrorBoundary>
                        );
                    default:
                        return null;
                }
            }
            return null;
        },
        [
            banners,
            getBannerSettings,
            COMMON_SITE_CONFIGS.casinoImg,
            reels,
            hasTopLeagues,
            t,
            prematchTopLeagues,
            setHoursRange,
        ],
    );

    return (
        <>
            <div className={styles.homePage}>{homePageConfig.widgets.map(renderSection)}</div>
            {homePageConfig.enableFAQ && <HomeFAQ />}
        </>
    );
};

export default Home;
